import React, { useEffect, useRef, useState } from 'react';

import css from './Reveal.module.css';

/**
 * Reveal — fade + Y-translate a block when it scrolls into view.
 *
 * Usage:
 *   <Reveal><SomeSection /></Reveal>
 *   <Reveal delay={120}><SomeCard /></Reveal>
 *   <Reveal as="section" className={css.mySection}>...</Reveal>
 *
 * Behavior:
 *   - Triggers once per element. Won't re-animate on scroll-back.
 *   - Honors `prefers-reduced-motion`: skips animation entirely and
 *     shows content in its final state immediately.
 *   - Uses IntersectionObserver with a 12% root-margin bias so the
 *     reveal fires slightly BEFORE the element enters the viewport,
 *     which reads as "already there" rather than "just popped in".
 *   - SSR-safe: on the server, `isVisible` stays false and the
 *     element renders with the initial transform; the layout effect
 *     on the client immediately re-triggers with the right state.
 */

const Reveal = ({
  as: Tag = 'div',
  children,
  className,
  delay = 0,
  ...rest
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReduced) {
      setIsVisible(true);
      return undefined;
    }

    const el = ref.current;
    if (!el) return undefined;

    // IntersectionObserver isn't available in very old browsers; fall
    // back to showing content immediately rather than hiding forever.
    if (typeof IntersectionObserver !== 'function') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -12% 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const classes = [css.root, isVisible ? css.visible : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag
      ref={ref}
      className={classes}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
